import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Checkbox, Input, message } from "antd";
import { CREATOR_LEGAL_AGREE_PROMPT } from "../../components/legal/LegalAgreePromptContent";
import { useAgreeGate } from "../../components/legal/UseAgreeGate";
import { ShellTopBar } from "../shell/ShellTopBar";
import { globalModel } from "../../global/GlobalModel";
import { useLongfansMock } from "../../global/GlobalMock";
import { openLoginCaptcha } from "../../utils/tianaiCaptcha";
import { CreatorApplyApi } from "./CreatorApplyApi";
import { creatorApplyCssVars } from "./Config";
import "./CreatorApplyPage.css";

const CODE_SECONDS = 60;
const API_BASE = () => process.env.REACT_APP_API_DOMAIN?.trim() ?? "";

const PUBLIC = process.env.PUBLIC_URL || "";

const FEATURES = [
  {
    image: `${PUBLIC}/bigv-concept-1.png`,
    imageAlt: "达人专属空间首页示意，展示与粉丝互动、内容发布的温馨场景",
    title: "专属空间，深度连接",
    desc: "每位达人拥有独立空间，粉丝专注你的内容，沟通更纯粹、关系更长久。",
  },
  {
    image: `${PUBLIC}/bigv-concept-2.png`,
    imageAlt: "财经工具界面组合，情报、笔记、持仓复盘等专业模块",
    title: "专业工具，高效分享",
    desc: "情报筛选、笔记沉淀、持仓复盘等专业模块，让知识输出更系统、更高效。",
  },
  {
    image: `${PUBLIC}/bigv-concept-3.png`,
    imageAlt: "合规理念示意图，强调不推荐个股、不承诺收益",
    title: "合规底线，长期同行",
    desc: "平台坚决不推荐股票、不承诺收益，守护达人与粉丝的长期信任。",
  },
] as const;

export default function CreatorApplyPage() {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [douyinAccount, setDouyinAccount] = useState("");
  const [channelsAccount, setChannelsAccount] = useState("");
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { runWithAgree, agreePromptModal } = useAgreeGate({
    agreed,
    setAgreed,
    uncheckedTip: CREATOR_LEGAL_AGREE_PROMPT.uncheckedTip,
  });

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const t = window.setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [cooldown]);

  const doSendCode = useCallback(
    async (captchaToken?: string) => {
      setSending(true);
      try {
        const ok = await CreatorApplyApi.sendApplyVerificationCode(phone, captchaToken);
        if (!ok) {
          return;
        }
        message.success("验证码已发送");
        setCode("");
        setCooldown(CODE_SECONDS);
      } finally {
        setSending(false);
      }
    },
    [phone],
  );

  const proceedSendCode = useCallback(async () => {
    if (!/^\d{11}$/.test(phone)) {
      message.warning("请输入11位手机号");
      return;
    }
    if (cooldown > 0 || sending) {
      return;
    }
    if (useLongfansMock) {
      await doSendCode();
      return;
    }
    const base = API_BASE();
    try {
      await openLoginCaptcha({
        genUrl: `${base}/lark/app/genCaptcha`,
        checkUrl: `${base}/lark/app/check`,
        onSuccess: (token) => doSendCode(token),
        onFail: () => message.error("滑动验证失败，请重试"),
      });
    } catch {
      message.error("验证码组件加载失败");
    }
  }, [phone, cooldown, sending, doSendCode]);

  const onSendCodeClick = useCallback(() => {
    runWithAgree(() => void proceedSendCode(), CREATOR_LEGAL_AGREE_PROMPT);
  }, [runWithAgree, proceedSendCode]);

  const onSubmit = useCallback(async () => {
    if (!/^\d{11}$/.test(phone)) {
      message.warning("请输入11位手机号");
      return;
    }
    if (!/^\d{4,6}$/.test(code.trim())) {
      message.warning("请输入短信验证码");
      return;
    }
    if (!douyinAccount.trim()) {
      message.warning("请填写抖音账号");
      return;
    }
    if (!channelsAccount.trim()) {
      message.warning("请填写视频号账号");
      return;
    }
    setSubmitting(true);
    try {
      const res = await CreatorApplyApi.submitApply({
        phoneNo: phone,
        code: code.trim(),
        douyinAccount: douyinAccount.trim(),
        channelsAccount: channelsAccount.trim(),
      });
      if (!res?.success) {
        message.error("提交失败，请检查验证码后重试");
        return;
      }
      setSubmitted(true);
      message.success("登记成功，我们会尽快与您联系");
    } finally {
      setSubmitting(false);
    }
  }, [phone, code, douyinAccount, channelsAccount]);

  const onSubmitClick = useCallback(() => {
    runWithAgree(() => void onSubmit(), CREATOR_LEGAL_AGREE_PROMPT);
  }, [runWithAgree, onSubmit]);

  return (
    <div className="creator-apply-root" style={creatorApplyCssVars()}>
      {agreePromptModal}
      <ShellTopBar onLoginClick={() => globalModel.openLoginModal()} />

      <section className="creator-apply-hero" aria-labelledby="creator-apply-title">
        <div className="creator-apply-hero-bg" aria-hidden />
        <div className="creator-apply-hero-inner">
          <div className="creator-apply-hero-copy">
            
            <h1 id="creator-apply-title" className="creator-apply-title">
               给每个财经达人一个与粉丝沟通、知识分享的地方，提供实用的财经工具，让知识分享更高效。
            </h1>
            <p className="creator-apply-eyebrow">LONGFANS · 达人入驻</p> 
          </div>
        </div>
      </section>

      <div className="creator-apply-body">
        <h2 className="creator-apply-section-title">平台理念</h2>
        <p className="creator-apply-section-sub">
          龙粉社为财经内容创作者提供可持续的知识分享阵地，知识+工具一体化分享。
        </p>

        <div className="creator-apply-features">
          {FEATURES.map((f) => (
            <article key={f.title} className="creator-apply-feature">
              <div className="creator-apply-feature-img">
                <img src={f.image} alt={f.imageAlt} />
              </div>
              <div className="creator-apply-feature-body">
                <h3 className="creator-apply-feature-title">{f.title}</h3>
                <p className="creator-apply-feature-desc">{f.desc}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="creator-apply-form-wrap">
          <div className="creator-apply-form-aside">
            <span className="creator-apply-form-badge">入驻登记</span>
            <h2 className="creator-apply-form-aside-title">开启您的专属空间</h2>
            <p className="creator-apply-form-lead">
              请留下您的联系方式与主流平台账号，专属顾问将在 1–3
              个工作日内与您联系，沟通空间开通事宜。
            </p>
            <ol className="creator-apply-form-steps">
              <li>填写手机号并完成验证</li>
              <li>提供抖音、视频号账号信息</li>
              <li>等待顾问回访，确认开通细节</li>
            </ol>
            <div className="creator-apply-form-eligibility">
              <span className="creator-apply-form-eligibility-label">入驻门槛</span>
              <p>目前仅面向抖音、视频号粉丝量达一万以上的财经类达人开放入驻。</p>
            </div>
          </div>

          <div className="creator-apply-form-card">
            {submitted ? (
              <div className="creator-apply-success">
                <div className="creator-apply-success-icon" aria-hidden>
                  ✓
                </div>
                <p className="creator-apply-success-title">登记已提交</p>
                <p className="creator-apply-success-desc">
                  感谢您的信任，专属顾问将尽快通过电话与您确认入驻细节。
                </p>
              </div>
            ) : (
              <>
                <div className="creator-apply-form-head">
                  <h3 className="creator-apply-form-title">填写入驻信息</h3>
                  <p className="creator-apply-form-sub">信息仅用于入驻审核与联系</p>
                </div>
                <div className="creator-apply-fields">
                  <Input
                    className="creator-apply-input"
                    size="large"
                    prefix={<span className="creator-apply-phone-prefix">+86</span>}
                    placeholder="手机号"
                    maxLength={11}
                    value={phone}
                    onChange={(e) =>
                      setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))
                    }
                    inputMode="numeric"
                    autoComplete="tel"
                  />
                  <div className="creator-apply-code-row">
                    <Input
                      className="creator-apply-input creator-apply-input--code"
                      size="large"
                      placeholder="短信验证码"
                      maxLength={6}
                      value={code}
                      onChange={(e) =>
                        setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                      }
                      inputMode="numeric"
                      autoComplete="one-time-code"
                    />
                    <Button
                      className={`creator-apply-btn-code longfans-rect-sm${!agreed ? " lf-btn-need-agree" : ""}`}
                      disabled={cooldown > 0}
                      loading={sending}
                      onClick={onSendCodeClick}
                    >
                      {cooldown > 0 ? `${cooldown}s` : "获取验证码"}
                    </Button>
                  </div>
                  <Input
                    className="creator-apply-input"
                    size="large"
                    placeholder="抖音账号"
                    value={douyinAccount}
                    onChange={(e) => setDouyinAccount(e.target.value)}
                    autoComplete="off"
                  />
                  <Input
                    className="creator-apply-input"
                    size="large"
                    placeholder="视频号账号"
                    value={channelsAccount}
                    onChange={(e) => setChannelsAccount(e.target.value)}
                    autoComplete="off"
                  />
                  <Checkbox
                    className="creator-apply-agree"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                  >
                    <span>
                      我已阅读并同意
                      <Link
                        className="creator-apply-agree-link"
                        to="/legal/creator"
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        《大V入驻规则》
                      </Link>
                    </span>
                  </Checkbox>
                </div>
                <div className="creator-apply-submit-wrap">
                  <Button
                    type="primary"
                    className={`creator-apply-submit${!agreed ? " lf-btn-need-agree" : ""}`}
                    loading={submitting}
                    onClick={onSubmitClick}
                  >
                    提交入驻登记
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
